import { typographyTokens } from '../../theme/typographyTokens';
import React, { useEffect, useState } from 'react';
import { BannerCarousel } from '../../components/BannerCarousel';
import { Box, Typography, Button, Divider, Drawer, Tab, Tabs, useTheme } from '@mui/material';
import SearchBar from '../../components/SearchBar/SearchBar';
import { useGetUserQuery } from '../../services/usersSlice';
import { useFindSepsQuery, useGetSubscribedSepsQuery, useUpdateSepSubscriptionsMutation } from '../../services/sepSlice';
import { ScreenName, SEPStatus, TableVisibleDataFields } from '../../../shared/types/SEP';
import SepTableHeader from '../../components/SepTable/SepTableHeader';
import SepTableBody from '../../components/SepTable/SepTableBody';
import PageNavigation from '../../components/PageNavigation/PageNavigation';
import ModalWidget from '../../components/ModalWidget/ModalWidget';
import EditColumnModal from '../../components/SepTable/EditColumnModal';
import SepSubscriptionsModal from '../../components/SepTable/SepSubscriptionsModal';
import { HeadCell, headCells } from '../../components/SepTable/SepTableBody';
import SepFilterBar from '../../components/SepFilterBar/SepFilterBar';
import SpinnerOverlay, { SpinnerSectionLoader } from '../../components/Spinner/Spinner';
import { SEPSearchRow } from '../../../shared/types/SEP';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetDataFieldTemplatesQuery } from '../../services/dataFieldsSlice';


// Phases that make up the default "In Progress" status filter
const IN_PROGRESS_PHASES = ['knockout', 'design', 'initiate', 'implement'];

const MySEPs = () => {
  const EDIT_COLUMNS_KEY = 'sharedSEPsEditColumns';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Reset only search
  const resetSearch = () => {
    setSearchText('');
    setSearchFilter('');
    setPage(0);
  };
  const [searchText, setSearchText] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortAsc, setSortAsc] = useState<boolean>(false);
  const [statusChecked, setStatusChecked] = useState<string[]>([...IN_PROGRESS_PHASES]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [visibleColumns, setVisibleColumns] = useState<HeadCell[]>([
    ...headCells,
  ]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [workFlowChecked, setWorkFlowChecked] = useState<string[]>([]);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<{
    status: string;
    workFlowType: string;
    myToDos: boolean;
    filterApplied: boolean;
  }>({ status: 'active', workFlowType: '', myToDos: false, filterApplied: true });
  const [isSubModalOpen, setIsSubModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [updateSepSubs] = useUpdateSepSubscriptionsMutation();
  const { data: dataFieldTemplate } = useGetDataFieldTemplatesQuery();
  const workFlowData = dataFieldTemplate?.find((data: any) => data?.name === 'Workflow data field');
  const workFlowNames: any = workFlowData?.dataFieldOptions?.map((val: any)=> val.value);
  const [visibleDataFieldsList, setVisibleDataFieldsList] = useState<any>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isEditColumnApplied, setIsEditColumnApplied] = useState<boolean>(false);
  const [myToDosChecked, setMyToDosChecked] = useState<boolean>(false);
    const [showTodo, setShowTodo] = useState<boolean>(true);
  
  // Restore filters from sessionStorage on mount
    useEffect(() => {
      const savedColumns = sessionStorage.getItem(EDIT_COLUMNS_KEY);
      
      // Parse and restore visible columns if saved
      let parsedVisibleColumns: HeadCell[] | null = null;
      
      if (savedColumns) {
        try {
          parsedVisibleColumns = JSON.parse(savedColumns);
          if (parsedVisibleColumns && Array.isArray(parsedVisibleColumns)) {
            setVisibleColumns(parsedVisibleColumns);
            const dataFieldsList = parsedVisibleColumns
              .filter((col: HeadCell) => col.visible && col.dbColumn && TableVisibleDataFields.includes(col.dbColumn))
              .map((col: HeadCell) => col.dbColumn);
            setVisibleDataFieldsList(dataFieldsList);
            
            // Use the same isDefault logic as in the visibleColumns useEffect for consistency
            const defaultVisibleColumns = headCells.filter(col => col.visible);
            const currentVisibleColumns = parsedVisibleColumns.filter((c: HeadCell) => c.visible);
            const isDefault = defaultVisibleColumns.length === currentVisibleColumns.length &&
              defaultVisibleColumns.every((col, index) => col.id === currentVisibleColumns[index]?.id);
            setIsEditColumnApplied(!isDefault);
          }
        } catch (e) {
          console.error('Error parsing saved columns from sessionStorage', e);
        }
      }
      // Mark initialization as complete
      setIsInitialized(true);
    }, []);

  useEffect(() => {
      // Only save to sessionStorage after initial mount/restore is complete
      if (isInitialized && visibleColumns && visibleColumns.length > 0) {
        const dataFieldColumns = visibleColumns.filter(col => col.visible && col.dbColumn && TableVisibleDataFields.includes(col.dbColumn)).map(col => col.dbColumn);
        setVisibleDataFieldsList(dataFieldColumns);
        
        // Compare current visible columns with default visible columns
        const defaultVisibleColumns = headCells.filter(col => col.visible);
        const currentVisibleColumns = visibleColumns.filter(c => c.visible);
        
        // Check if both arrays have the same length and same column IDs in the same order
        const isDefault = defaultVisibleColumns.length === currentVisibleColumns.length &&
          defaultVisibleColumns.every((col, index) => col.id === currentVisibleColumns[index]?.id);
        
        setIsEditColumnApplied(!isDefault);
        if(!isDefault){
          sessionStorage.setItem(EDIT_COLUMNS_KEY, JSON.stringify(visibleColumns));
        }else{
          sessionStorage.removeItem(EDIT_COLUMNS_KEY);
        }
      }
    }, [visibleColumns, isInitialized]);
  

  const updateStatuses = (statuses: any) => {
    setStatusChecked(statuses);
  };

  const handleUnsubscribe = async (sepID : number,subscribers: string) => {
    const userEmail = ""+loggedInUser?.email
    const multipleEntry = subscribers.includes(","+userEmail)
    const updatedSubscriberList = multipleEntry? subscribers.replace(","+userEmail,"") :  subscribers.replace(userEmail,"")

    if (sepID > 0) {
      await updateSepSubs({
        id: sepID,
        body: { subscribers: updatedSubscriberList, action: "unsubscribed" } as any,
      });
    }
    refetch()
  };

  const handleSubscriptionChange = async (sep: SEPSearchRow) => {
    const userEmail = loggedInUser?.email;
    if (!userEmail) return;

    const subscribers = (sep.subscribers || '')
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean);
    const isSubscribed = subscribers.includes(userEmail);
    const updatedSubscribers = isSubscribed
      ? subscribers.filter((email) => email !== userEmail)
      : [...subscribers, userEmail];

    await updateSepSubs({
      id: sep.id,
      body: {
        subscribers: updatedSubscribers.join(','),
        action: isSubscribed ? 'unsubscribed' : 'subscribed',
      } as any,
    });
    await refetch();
  };
  const {
    data: loggedInUser,
    error: getUserError,
    isLoading: getUserIsLoading,
  } = useGetUserQuery('me');

  const statusArray = Object.values(SEPStatus)
    .map((status) => status.toString())
    .join(',');

  const getStatusList = (statuses: any[]) =>
    statuses
      .map((phase: any) => {
        // if phase equals knockout return active status
        if (
          phase === 'knockout' ||
          phase === 'design' ||
          phase === 'implement' ||
          phase === 'initiate'
        ) {
          return 'active';
        }
        // if phase equals complete return approved status
        else if (phase === 'complete') {
          return 'approved';
        }
        // return phase if it's included in statusArray; otherwise, return null
        return statusArray.includes(phase) ? phase : null;
      })
      .filter((status) => status !== null)
      .join(',');

    const updateWorkFlowType = (type: any) => {
      setWorkFlowChecked(type);
    };

    const getWorkFlowTypeList = (types: any[]) =>
      types
        .map((workFlowType: any) => {
          return workFlowNames?.includes(workFlowType) ? workFlowType : null;
        })
        .filter((status) => status !== null)
        .join(',');

    const applyFilters = () => {
      const status = getStatusList(statusChecked);
      const workFlowType = getWorkFlowTypeList(workFlowChecked);
      setAppliedFilters({
        status,
        workFlowType,
        myToDos: myToDosChecked,
        filterApplied: Boolean(status || workFlowType || myToDosChecked),
      });
      setPage(0);
      setIsFilterDrawerOpen(false);
    };

    const resetFilters = () => {
      setStatusChecked([]);
      setWorkFlowChecked([]);
      setMyToDosChecked(false);
      setAppliedFilters({ status: '', workFlowType: '', myToDos: false, filterApplied: false });
      setPage(0);
      setIsFilterDrawerOpen(false);
    };

  const { data, isLoading: isLoadingSEPData, isFetching: isFetchingSEPData, isError: isErrorSEPData } = useFindSepsQuery({
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    sortBy,
    sortAsc,
    search: searchFilter,
    mine: true,
    myToDos: appliedFilters.myToDos,
    status: appliedFilters.status,
    workFlowType: appliedFilters.workFlowType,
    dataFieldColumns: visibleDataFieldsList.join(','),
    filterApplied: true,
  });

  const { data: subscribedSeps, isError:subError, isLoading:subLoading, error, refetch } = useGetSubscribedSepsQuery(
    loggedInUser?.email || skipToken
  );
  const subscriptionRows: SEPSearchRow[] = (subscribedSeps || []).map((sep) => ({
    ...sep,
    creator: sep.creator,
    tasks: sep.tasks,
    userNotesCount: 0,
  }));

  useEffect(() => {

    refetch();

  }, [refetch, loggedInUser]);
  useEffect(() => {
    // Debounce our search function here so we can wait 500ms for the user to stop typing
    const delayDebounceFn = setTimeout(() => {
      if (searchText.length >= 3) {
        setSearchFilter(searchText);
      } else {
        setSearchFilter('');
      }
      // Changing searchFilter should reset pagination.
      setPage(0);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchText, setSearchFilter]);

  return (
    <Box display="flex" flexDirection="column" flexGrow={1} minHeight={0} height="100%" overflow="hidden" pt="24px">
      <ModalWidget
        isOpen={isOpen}
        title="Edit Columns"
        children={
          <EditColumnModal
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            closeModal={() => setIsOpen(false)}
            defaultColumns={[...headCells]}
            isModalOpen={isOpen}
          />
        }
        onClose={() => setIsOpen(false)}
        backgroundColor={isDarkMode ? theme.palette.background.default : theme.palette.supportingAdditional.tealUltraLight}
        textColor={isDarkMode ? theme.palette.text.primary : theme.palette.content.default}
      />
      <ModalWidget
        isOpen={isSubModalOpen}
        title="SEP Subscriptions"
        children={
          <SepSubscriptionsModal
            subscribedSeps={subscribedSeps} handleSave={handleUnsubscribe}
          />
        }
        onClose={() => setIsSubModalOpen(false)}
      />
      <SearchBar
        title="My SEPs"
        searchText={searchText}
        setSearchText={setSearchText}
        reset={resetSearch}
      />
      <Drawer
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        PaperProps={{ sx: { backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.supportingAdditional.tealUltraLight } }}
      >
        <Box sx={{ width: 'auto', padding: '12px', color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default }} role="presentation">
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mb: 2 }}>
            <Typography
              component="span"
              sx={{
                fontSize: typographyTokens.fontSizes.xl,
                fontWeight: typographyTokens.fontWeights.bold,
              }}
            >
              Filters
            </Typography>
            <Button onClick={() => setIsFilterDrawerOpen(false)}>
              <i style={{ color: 'gray' }} className="fa fa-close"></i>
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <SepFilterBar
            statusChecked={statusChecked}
            setStatusChecked={updateStatuses}
            showTodo={showTodo}
            setShowTodo={setShowTodo}
            myToDosChecked={myToDosChecked}
            setMyToDosChecked={setMyToDosChecked}
            data={data}
            filterPage={'mysep'}
            workFlowChecked={workFlowChecked}
            setWorkFlowChecked={updateWorkFlowType}
            finalData={workFlowNames}
          />
          <Box sx={{ display: 'flex', mt: 3, justifyContent: 'flex-end', px: 2, gap: 2 }}>
            <Button
              size="large"
              variant="contained"
              sx={{
                textTransform: 'none',
                backgroundColor: '#2372B9',
                font: 'normal normal 600 16px/22px Open Sans',
              }}
              onClick={applyFilters}
            >
              Apply
            </Button>
            <Button
              size="large"
              variant="outlined"
              sx={{
                textTransform: 'none',
                font: 'normal normal 600 16px/22px Open Sans',
              }}
              onClick={resetFilters}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Drawer>
      {/* <Button onClick={() => { setIsSubModalOpen(true) }}>SEP Subscriptions</Button> */}
      <BannerCarousel />

      {isLoadingSEPData || getUserIsLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexGrow={1}
          py="24px"
        >
          <SpinnerOverlay loaderText='' showSpinner={false} />
          <SpinnerSectionLoader
            loaderText="Loading SEPs..."
            showSpinner={true} />
        </Box>
      ) : isErrorSEPData || getUserError ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexGrow={1}
          py="24px"
        >
          <Typography color="error.main" fontSize={typographyTokens.fontSizes.h5}>
            Error
          </Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
          <SepTableHeader
            count={activeTab === 1 ? subscriptionRows.length : data?.count ?? 0}
            sortBy={sortBy}
            sortAsc={sortAsc}
            status={statusChecked
              .map((phase: any) => phase.toString())
              .join(',')}
            searchFilter={searchFilter}
            resultNumber={activeTab === 1 ? subscriptionRows.length : data?.count ?? 0}
            assigneeId={loggedInUser?.id}
            onModalOpen={() => setIsOpen(true)}
            hideExportButton={true}
            loading={isFetchingSEPData}
            isEditColumnApplied={isEditColumnApplied}
            toggleDrawer={setIsFilterDrawerOpen}
            appliedFilters={appliedFilters}
            hideFilterButton={activeTab === 1}
            hideEditColumn={activeTab === 1}
            tabs={
              <Tabs
                value={activeTab}
                onChange={(_, value) => {
                  setActiveTab(value);
                  setPage(0);
                }}
                aria-label="SEP request tabs"
                TabIndicatorProps={{ sx: { display: 'none' } }}
                sx={{
                  minHeight: 'auto',
                  '& .MuiTab-root': {
                    minHeight: 'auto',
                    px: 2,
                    py: '6px',
                    border: '1px solid',
                    borderColor: theme.palette.supportingAdditional.tealContainerStroke,
                    borderRadius: 0,
                    color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default,
                    textTransform: 'uppercase',
                  },
                  '& .MuiTab-root:not(:first-of-type)': {
                    marginLeft: '-1px',
                  },
                  '& .MuiTab-root:first-of-type': {
                    borderRadius: '6px 0 0 6px',
                  },
                  '& .MuiTab-root:last-of-type': {
                    borderRadius: '0 6px 6px 0',
                  },
                  '& .MuiTab-root.Mui-selected': {
                    zIndex: 1,
                    borderColor: theme.palette.prime.orange,
                    backgroundColor: theme.palette.prime.orange,
                    color: theme.palette.content.white,
                  },
                }}
              >
                <Tab label="My Requests" />
                <Tab label="My Subscriptions" />
              </Tabs>
            }
          />
          {isFetchingSEPData ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexGrow={1}
              py="24px"
            >
              <SpinnerOverlay loaderText='' showSpinner={false} />
              <SpinnerSectionLoader
                loaderText="Loading SEPs..."
                showSpinner={true} />
            </Box>
          ) : isErrorSEPData ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexGrow={1}
              py="24px"
            >
              <Typography color="error.main" fontSize={typographyTokens.fontSizes.h5}>
                Error
              </Typography>
            </Box>
          ) : (
            <SepTableBody
            rows={activeTab === 1 ? subscriptionRows : data?.seps || []}
            count={activeTab === 1 ? subscriptionRows.length : data?.count ?? 0}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortAsc={sortAsc}
              setSortAsc={setSortAsc}
              headers={[...(activeTab !== 1 ? visibleColumns : [...headCells.filter(col => col.visible), { id: 'subscription', key: '', label: 'Subscription', visible: true }])]}
              loggedInUser={activeTab !== 1 ? loggedInUser?.id : ''}
              currentUserEmail={loggedInUser?.email}
              screenName={ScreenName.mySEP}
              onSubscriptionChange={handleSubscriptionChange}
            />
          )}
          <PageNavigation
              count={activeTab === 1 ? subscriptionRows.length : data?.count ?? 0}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
        </Box>
      )}
    </Box>
  );
};

export default MySEPs;


